import { useEffect, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { cn } from '@/lib/utils';

export interface FileUploadConfig {
  queryKey: string[];
  /**
   * Direct URL for the already-fetched file. When provided, bypasses the internal
   * query/validation layer entirely — use this when the URL lives on another query
   * (e.g. piggybacked on a user-profile response) to avoid double-caching a presigned
   * URL that may expire before the internal 5-min staleTime elapses.
   */
  fileUrl?: string | null;
  getFileUrl?: () => Promise<string>;
  getUploadUrl?: ({
    filename,
    fileSize,
  }: {
    filename: string;
    fileSize: number;
  }) => Promise<string>;
  deleteFile?: () => Promise<void>;
  onUploadSuccess?: () => void;
  onUploadError?: (error: Error) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: Error) => void;
  allowedTypes?: string[];
  maxSize?: number;
  accept?: string;
  /** How long fetched file data stays fresh (ms). Defaults to 5 minutes. */
  staleTime?: number;
}

interface FileValidationParams {
  file: File;
  allowedTypes?: string[];
  maxSize?: number;
  invalidTypeMessage: string;
  invalidSizeMessage: string;
}

interface FileMetadataResult {
  url: string | null;
  filename: string | null;
}

interface FileUploadFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  helpText?: string;
  config?: FileUploadConfig;
  texts?: {
    loading?: string;
    error?: string;
    noFileChosen?: string;
    uploading?: string;
    chooseFile?: string;
    deleteFile?: string;
    invalidType?: string;
    invalidSize?: string;
  };
  /** Called when file validation error state changes (true = has error, false = no error) */
  onValidationErrorChange?: (hasError: boolean) => void;
}

const DEFAULT_FILE_STALE_TIME = 5 * 60 * 1000;

const DEFAULT_FILE_CONFIG: FileUploadConfig = {
  queryKey: [],
  allowedTypes: ['image/jpeg', 'image/png', 'image/jpg'],
  maxSize: 2 * 1024 * 1024,
  accept: 'image/jpeg,image/png,image/jpg',
  staleTime: DEFAULT_FILE_STALE_TIME,
};

/**
 * Validates uploaded file against type and size constraints.
 * Provides localized error messages for validation failures.
 */
const validateFile = ({
  file,
  allowedTypes,
  maxSize,
  invalidTypeMessage,
  invalidSizeMessage,
}: FileValidationParams): void => {
  if (allowedTypes && !allowedTypes.includes(file.type)) {
    throw new Error(invalidTypeMessage);
  }

  if (maxSize && file.size > maxSize) {
    throw new Error(invalidSizeMessage);
  }
};

/**
 * Extracts filename from S3 response headers or URL path.
 * Prioritizes custom metadata over URL-based extraction.
 */
const extractFilenameFromUrl = (url: string): string | null => {
  try {
    const parts = new URL(url).pathname.split('/');
    return parts[parts.length - 1] || null;
  } catch {
    return null;
  }
};

const extractFilename = (response: Response, url: string): string | null => {
  const headerFilename = response.headers.get('x-amz-meta-filename');

  if (headerFilename) {
    return headerFilename;
  }

  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    return pathParts[pathParts.length - 1] || null;
  } catch {
    return null;
  }
};

/**
 * A comprehensive file upload component with support for validation, preview, and cloud storage.
 * Integrates with react-hook-form and provides configurable upload/download capabilities.
 *
 * Features:
 * - File type and size validation
 * - Image preview for uploaded files
 * - Cloud storage integration (S3 compatible)
 * - Loading states and error handling
 * - Internationalization support
 * - Configurable upload/delete operations
 * - Form validation integration
 *
 * @example
 * ```tsx
 * // Basic file upload for form field
 * const form = useForm({
 *   defaultValues: {
 *     avatar: null
 *   }
 * });
 *
 * <FileUploadField
 *   name="avatar"
 *   control={form.control}
 *   label="Profile Picture"
 *   helpText="Upload JPG, PNG up to 2MB"
 * />
 *
 * // With cloud storage integration
 * <FileUploadField
 *   name="projectLogo"
 *   control={form.control}
 *   label="Project Logo"
 *   config={{
 *     queryKey: ['project-logo', projectId],
 *     getFileUrl: () => getProjectLogo(projectId),
 *     getUploadUrl: ({ filename, fileSize }) =>
 *       getUploadUrl({ projectId, filename, fileSize }),
 *     deleteFile: () => deleteProjectLogo(projectId),
 *     allowedTypes: ['image/png', 'image/svg+xml'],
 *     maxSize: 1024 * 1024, // 1MB
 *     onUploadSuccess: () => {
 *       toast.success('Logo uploaded successfully');
 *       queryClient.invalidateQueries(['projects']);
 *     }
 *   }}
 * />
 *
 * // Custom validation and text overrides
 * <FileUploadField
 *   name="document"
 *   control={form.control}
 *   label="Contract Document"
 *   config={{
 *     allowedTypes: ['application/pdf', 'application/msword'],
 *     maxSize: 10 * 1024 * 1024, // 10MB
 *     accept: '.pdf,.doc,.docx'
 *   }}
 *   texts={{
 *     chooseFile: 'Select Contract',
 *     invalidType: 'Please upload PDF or Word document',
 *     invalidSize: 'Document must be smaller than 10MB'
 *   }}
 *   disabled={isSubmitting}
 * />
 * ```
 */
export function FileUploadField<T extends FieldValues>({
  name,
  control,
  label,
  className,
  disabled = false,
  placeholder,
  helpText,
  config = DEFAULT_FILE_CONFIG,
  texts,
  onValidationErrorChange,
}: FileUploadFieldProps<T>) {
  const { t } = useTranslation(['common']);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Notify parent when validation error state changes
  useEffect(() => {
    onValidationErrorChange?.(!!error);
  }, [error, onValidationErrorChange]);

  const mergedTexts = {
    loading: texts?.loading || t('common:fileUpload.loading'),
    error: texts?.error || t('common:fileUpload.error'),
    noFileChosen: texts?.noFileChosen || t('common:fileUpload.noFileChosen'),
    uploading: texts?.uploading || t('common:fileUpload.uploading'),
    chooseFile: texts?.chooseFile || t('common:fileUpload.chooseFile'),
    deleteFile: texts?.deleteFile || t('common:fileUpload.deleteFile'),
    invalidType: texts?.invalidType || t('common:fileUpload.invalidType'),
    invalidSize: texts?.invalidSize || t('common:fileUpload.invalidSize'),
  };
  const mergedConfig = { ...DEFAULT_FILE_CONFIG, ...config };

  const hasDirectUrl = mergedConfig.fileUrl !== undefined;

  const {
    data: fileData,
    isLoading: isLoadingFile,
    isError: isFileError,
    refetch: refetchFile,
  } = useQuery({
    queryKey: mergedConfig.queryKey,
    queryFn: async (): Promise<FileMetadataResult> => {
      if (!mergedConfig.getFileUrl) {
        return { url: null, filename: null };
      }

      try {
        const url = await mergedConfig.getFileUrl();

        const response = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
        });

        if (response.status === 404) {
          return { url: null, filename: null };
        }

        const filename = extractFilename(response, url);

        return { url, filename };
      } catch {
        return { url: null, filename: null };
      }
    },
    enabled: !hasDirectUrl && !!mergedConfig.getFileUrl,
    staleTime: mergedConfig.staleTime,
  });

  const directUrl = hasDirectUrl ? (mergedConfig.fileUrl ?? null) : null;
  const directFilename = directUrl ? extractFilenameFromUrl(directUrl) : null;

  const fileUrl = hasDirectUrl ? directUrl : (fileData?.url ?? null);
  const filename = hasDirectUrl ? directFilename : (fileData?.filename ?? null);

  const deleteFileMutation = useMutation({
    mutationFn: mergedConfig.deleteFile || (() => Promise.resolve()),
    onSuccess: async () => {
      queryClient.setQueryData<FileMetadataResult>(mergedConfig.queryKey, {
        url: null,
        filename: null,
      });
      setError(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      mergedConfig.onDeleteSuccess?.();
    },
    onError: (err: Error) => {
      setError(err.message || t('common:fileUpload.failedToDelete'));
      mergedConfig.onDeleteError?.(err);
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (file: File) => {
      validateFile({
        file,
        allowedTypes: mergedConfig.allowedTypes,
        maxSize: mergedConfig.maxSize,
        invalidTypeMessage: mergedTexts.invalidType,
        invalidSizeMessage: mergedTexts.invalidSize,
      });

      if (!mergedConfig.getUploadUrl) {
        throw new Error(t('common:fileUpload.uploadNotConfigured'));
      }

      const uploadUrl = await mergedConfig.getUploadUrl({
        filename: file.name,
        fileSize: file.size,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(t('common:fileUpload.uploadFailed', { status: uploadResponse.status }));
      }

      return true;
    },
    onSuccess: async () => {
      setError(null);
      mergedConfig.onUploadSuccess?.();
      await queryClient.invalidateQueries({ queryKey: mergedConfig.queryKey });
    },
    onError: (err: Error) => {
      setError(err.message || t('common:fileUpload.failedToUpload'));
      mergedConfig.onUploadError?.(err);
    },
    onSettled: () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
  });

  /**
   * Handles file selection with client-side pre-validation.
   * Validates file type and size before setting form value or triggering upload.
   */
  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (file: File | null) => void
  ) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      fieldOnChange(null);
      return;
    }

    // Always validate file type and size before accepting
    try {
      validateFile({
        file,
        allowedTypes: mergedConfig.allowedTypes,
        maxSize: mergedConfig.maxSize,
        invalidTypeMessage: mergedTexts.invalidType,
        invalidSizeMessage: mergedTexts.invalidSize,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : mergedTexts.invalidType);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      return;
    }

    setError(null);
    fieldOnChange(file);

    if (mergedConfig.getUploadUrl) {
      uploadFileMutation.mutate(file);
    }
  };

  /**
   * Handles file deletion from both form field and server.
   * Always clears the form field value and cancels any in-progress upload.
   */
  const handleFileDelete = (fieldOnChange: (file: File | null) => void) => {
    // Always clear the form field value to ensure immediate UI update
    fieldOnChange(null);

    // Cancel any in-progress upload to prevent it from re-setting the file URL
    if (uploadFileMutation.isPending) {
      uploadFileMutation.reset();
    }

    // Clear the file input element
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (fileUrl && mergedConfig.deleteFile) {
      deleteFileMutation.mutate();
    } else {
      // Clear cache when no server-side file exists
      queryClient.setQueryData<FileMetadataResult>(mergedConfig.queryKey, {
        url: null,
        filename: null,
      });
      setError(null);
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <div
              className={cn(
                'flex justify-between items-center w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background gap-2',
                error && 'border-destructive',
                disabled && 'bg-muted'
              )}
            >
              {isLoadingFile ? (
                <span className="text-sm text-muted-foreground">{mergedTexts.loading}</span>
              ) : isFileError ? (
                <span className="text-sm text-destructive">{mergedTexts.error}</span>
              ) : fileUrl ? (
                <div className="flex min-w-0 items-center gap-2">
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <img
                      src={fileUrl}
                      alt={t('common:fileUpload.previewAlt')}
                      className="size-9 rounded object-cover"
                    />
                  </a>
                  <div className="truncate text-sm text-foreground">{filename}</div>
                </div>
              ) : field.value ? (
                <div className="flex min-w-0 items-center gap-2">
                  {field.value.type?.startsWith('image/') && (
                    <img
                      src={URL.createObjectURL(field.value)}
                      alt={t('common:fileUpload.previewAlt')}
                      className="size-9 shrink-0 rounded object-cover"
                    />
                  )}
                  <div className="truncate text-sm text-foreground">{field.value.name}</div>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {placeholder || mergedTexts.noFileChosen}
                </span>
              )}

              <input
                type="file"
                accept={mergedConfig.accept}
                ref={fileInputRef}
                className="sr-only"
                onChange={e => handleFileSelect(e, field.onChange)}
                disabled={disabled}
              />

              {fileUrl || field.value ? (
                <Button
                  type="button"
                  variant="ghostDestructive"
                  size="sm"
                  className="shrink-0"
                  onClick={() => handleFileDelete(field.onChange)}
                  disabled={
                    disabled || deleteFileMutation.isPending || uploadFileMutation.isPending
                  }
                >
                  {mergedTexts.deleteFile}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  className="shrink-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || uploadFileMutation.isPending}
                >
                  {uploadFileMutation.isPending ? mergedTexts.uploading : mergedTexts.chooseFile}
                </Button>
              )}
            </div>
          </FormControl>

          {error ? (
            <div className="mt-2 text-xs text-destructive">{error}</div>
          ) : (
            <div className="mt-2 text-xs text-muted-foreground">
              {helpText || t('common:fileUpload.helpText')}
            </div>
          )}

          <FormMessage />
        </FormItem>
      )}
    />
  );
}
